/**
 * A numeric PIN field (§7, §36).
 *
 * One input, not four boxes. The boxed variant looks smarter and is worse:
 * it fights password managers, it breaks paste, and every implementation of
 * it has to reinvent backspace, arrow keys and screen-reader announcements.
 * A single input with `inputMode="numeric"` opens the same keypad, reads as
 * one field, and behaves the way the platform already knows how to behave.
 *
 * `type="password"` rather than a custom mask, so the browser obscures it and
 * assistive technology announces it as a password field — a parent typing a
 * PIN with a child beside them is the whole reason this exists.
 */

import { forwardRef, type InputHTMLAttributes } from 'react';

import { cn } from '@/lib/cn';

export interface PinInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'inputMode'> {
  label: string;
  /** Rendered under the field, and wired up with aria-describedby. */
  hint?: string;
  invalid?: boolean;
}

export const PinInput = forwardRef<HTMLInputElement, PinInputProps>(function PinInput(
  { label, hint, invalid = false, id, className, onChange, ...rest },
  ref,
) {
  const inputId = id ?? rest.name ?? 'pin';
  const hintId = hint ? `${inputId}-hint` : undefined;

  return (
    <div className="flex flex-col gap-[var(--space-2)]">
      <label htmlFor={inputId} className="font-[var(--weight-semibold)]">
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        type="password"
        // `numeric`, not `tel`: tel adds *, # and + to the keypad, none of
        // which can appear in a PIN.
        inputMode="numeric"
        autoComplete="off"
        // Off, deliberately. A saved PIN on the child's device defeats it.
        data-1p-ignore="true"
        pattern="[0-9]*"
        aria-invalid={invalid || undefined}
        aria-describedby={hintId}
        onChange={(event) => {
          // Strip anything that is not a digit as it is typed, so the field
          // can never hold a value the backend will reject.
          const cleaned = event.target.value.replace(/\D/g, '');
          if (cleaned !== event.target.value) event.target.value = cleaned;
          onChange?.(event);
        }}
        className={cn(
          'min-h-[var(--tap-primary)] rounded-[var(--radius-lg)] border bg-[var(--color-surface)]',
          'px-4 text-center text-[length:var(--text-2xl)] tracking-[0.4em]',
          invalid ? 'border-[var(--color-danger)]' : 'border-[var(--color-border)]',
          className,
        )}
        {...rest}
      />
      {hint ? (
        <p id={hintId} className="text-[length:var(--text-sm)] text-[var(--color-text-muted)]">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
